import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import GalaxyVideoPlayer from './GalaxyVideoPlayer';
import { galaxyVideoPermissions, isGalaxyVideo, parseGalaxyVideoUrl, publicationRevision } from './galaxyVideoUrl';
import './galaxyVideo.css';

const EMPTY = { kind: 'empty', url: '' };
const ACK_DEADLINE_MS = 5000;

const GalaxyVideoSession = forwardRef(function GalaxyVideoSession({
  galaxy, player, socket, refetchGalaxy, onSaved, onVisibilityChange,
  showAd, ad, renderUpload, permissions,
}, ref) {
  const [snapshot, setSnapshot] = useState(null);
  const [visible, setVisible] = useState(false);
  const [playRequest, setPlayRequest] = useState(null);
  const [playerEpoch, setPlayerEpoch] = useState(0);
  const [draft, setDraft] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [fieldError, setFieldError] = useState('');
  const [retry, setRetry] = useState(null);
  const [viewerRecovery, setViewerRecovery] = useState(null);
  const [refreshingVideo, setRefreshingVideo] = useState(false);
  const live = useRef(true);
  const current = useRef(null);
  const initialized = useRef(false);
  const consumed = useRef(0);
  // Keep the newest delivery (and its original viewer choice) across failed reads.
  // Observing it does not mean its saved source has been handled.
  const delivery = useRef(null);
  const catchingUp = useRef(false);
  const choice = useRef(0);
  const draftVersion = useRef(0);
  const submitting = useRef(false);
  const options = useRef(null);
  const pending = useRef(null);
  const cancelAcks = useRef(new Set());
  options.current = { galaxy, player, socket, refetchGalaxy, onSaved, permissions };

  useLayoutEffect(() => {
    live.current = true;
    const acknowledgements = cancelAcks.current;
    return () => { live.current = false; acknowledgements.forEach((cancel) => cancel()); };
  }, []);

  const accept = useCallback((next) => {
    if (!live.current || !next || next._id !== options.current.galaxy?._id) return false;
    const revision = publicationRevision(next);
    if (current.current && revision < publicationRevision(current.current)) return false;
    const sourceChanged = current.current?.coverMedia !== next.coverMedia;
    const newer = revision > publicationRevision(current.current);
    if (!initialized.current) {
      initialized.current = true;
      consumed.current = revision;
      setVisible(options.current.permissions.view && isGalaxyVideo(parseGalaxyVideoUrl(next.coverMedia)));
    }
    current.current = next;
    setSnapshot(next);
    setViewerRecovery((old) => old?.event && revision > old.event.revision ? null : old);
    if (sourceChanged || newer) {
      const cancelPending = pending.current !== null;
      pending.current = null; setPlayRequest(null);
      if (sourceChanged || cancelPending) setPlayerEpoch((value) => value + 1);
      setRetry((old) => old && old.revision !== revision ? null : old);
    }
    return true;
  }, []);

  useLayoutEffect(() => { accept(galaxy); }, [galaxy, accept]); // Snapshot identity never resets viewer choices.
  useEffect(() => { onVisibilityChange?.(visible && permissions.view); }, [visible, permissions.view, onVisibilityChange]);

  const refresh = useCallback(async (catchUp = false, expectedDelivery = null) => {
    const result = await options.current.refetchGalaxy?.();
    if (!live.current || (expectedDelivery && delivery.current !== expectedDelivery)) return null;
    if (result?.isError || result?.error || !result?.data || result.data._id !== options.current.galaxy?._id) throw new Error('Galaxy refresh unavailable');
    accept(result.data);
    if (catchUp) consumed.current = Math.max(consumed.current, publicationRevision(current.current));
    return result.data;
  }, [accept]);
  const catchUp = useCallback(async () => {
    if (catchingUp.current) return;
    catchingUp.current = true; setRefreshingVideo(true);
    const beforeDelivery = delivery.current;
    try {
      await refresh(true);
      if (live.current) setViewerRecovery((old) => !old?.event || old.event.revision <= consumed.current ? null : old);
    } catch (_) {
      if (live.current && delivery.current === beforeDelivery) setViewerRecovery({ event: null });
    } finally {
      catchingUp.current = false;
      if (live.current && !delivery.current?.loading) setRefreshingVideo(false);
    }
  }, [refresh]);
  useImperativeHandle(ref, () => ({ catchUp }), [catchUp]);

  const loadPublication = useCallback(async (request) => {
    if (!live.current || !options.current.permissions.view || delivery.current !== request || request.loading) return;
    const { event, beforeChoice } = request;
    const revision = event.revision;
    request.loading = true; setRefreshingVideo(true);
    try {
      const saved = await refresh(false, request);
      if (!live.current || delivery.current !== request || !options.current.permissions.view) return;
      if (publicationRevision(current.current) > revision) {
        consumed.current = Math.max(consumed.current, revision); setViewerRecovery(null); return;
      }
      if (saved?._id !== options.current.galaxy?._id || publicationRevision(saved) !== revision ||
          publicationRevision(current.current) !== revision) throw new Error('Publication source not current');
      const alreadyHandled = revision <= consumed.current;
      consumed.current = Math.max(consumed.current, revision); setViewerRecovery(null);
      if (alreadyHandled || beforeChoice !== choice.current || event.showVideo !== true || event.intent !== 'go-live' ||
          saved.coverMediaPublication?.intent !== 'go-live' || !isGalaxyVideo(parseGalaxyVideoUrl(saved.coverMedia))) return;
      // React 17 does not batch this asynchronous continuation. Set the request
      // before mounting/replacing the player, which initializes from autoPlay.
      pending.current = revision; setPlayRequest(revision);
      setPlayerEpoch((value) => value + 1); setPreview(null); setVisible(true);
    } catch (_) {
      if (live.current && delivery.current === request && revision > consumed.current &&
          publicationRevision(current.current) <= revision) setViewerRecovery(request);
    } finally {
      request.loading = false;
      if (live.current && delivery.current === request && !catchingUp.current) setRefreshingVideo(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (!galaxy?._id || !socket) return undefined;
    const receive = async (event) => {
      if (event?.galaxyId !== options.current.galaxy?._id || !options.current.permissions.view) return;
      const revision = event.revision;
      if (!Number.isSafeInteger(revision)) { await catchUp(); return; }
      if (revision < publicationRevision(current.current) || revision <= consumed.current ||
          revision < (delivery.current?.event.revision || 0)) return;
      if (delivery.current?.event.revision !== revision) {
        delivery.current = { event, beforeChoice: choice.current, loading: false };
        setViewerRecovery(null);
      }
      await loadPublication(delivery.current);
    };
    socket.on('getShowVideo', receive);
    return () => socket.off('getShowVideo', receive);
  }, [galaxy?._id, socket, catchUp, loadPublication]);

  function cancelPlayback() {
    choice.current++;
    // Discard a not-yet-playing SDK so a late ready callback cannot undo Pause.
    if (pending.current !== null) setPlayerEpoch((value) => value + 1);
    pending.current = null; setPlayRequest(null);
  }
  function broadcast(publication) {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (ack) => {
        if (settled) return;
        settled = true; clearTimeout(timer); cancelAcks.current.delete(cancel);
        resolve(ack?.success === true && ack.revision === publication.revision && ack.intent === publication.intent);
      };
      const cancel = () => finish(null);
      const timer = setTimeout(cancel, ACK_DEADLINE_MS);
      cancelAcks.current.add(cancel);
      const connection = options.current.socket;
      if (!connection || connection.connected === false) { cancel(); return; }
      try { connection.emit('sendShowVideo', { galaxyId: publication.galaxyId, showVideo: true, revision: publication.revision }, finish); }
      catch (_) { cancel(); }
    });
  }
  async function publishSaved(saved) {
    const publication = saved.coverMediaPublication;
    if (!publication || !Number.isSafeInteger(publication.revision) || publication.revision < 1) {
      setNotice('Saved. Live updates are unavailable until the video service is updated.'); return;
    }
    const request = { galaxyId: saved._id, revision: publication.revision, intent: publication.intent };
    const confirmed = await broadcast(request);
    if (!live.current || publicationRevision(current.current) !== request.revision) return;
    setRetry(confirmed ? null : request);
    setNotice(confirmed ? (request.intent === 'go-live' ? 'Saved. Live update sent; playback depends on each viewer’s browser.' : 'Saved. Video update sent.')
      : 'Saved; live update not confirmed. You can retry sending this update.');
  }
  async function save(intent) {
    if (submitting.current || !options.current.permissions.save || (intent === 'go-live' && !options.current.permissions.live)) return;
    const media = parseGalaxyVideoUrl(draft);
    if (!isGalaxyVideo(media)) { setFieldError('Enter a YouTube video or a supported HTTPS video/stream link.'); return; }
    const draftAtSubmit = draftVersion.current;
    const identity = options.current.galaxy._id;
    const revisionAtSubmit = publicationRevision(current.current);
    submitting.current = true; setBusy(true); setFieldError(''); setNotice('Saving…'); setRetry(null); setPreview(null);
    try {
      const response = await axiosConn.patch(`galaxies/${identity}`, { coverMedia: media.url, coverMediaIntent: intent }, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${options.current.player.token}` },
      });
      if (!live.current) return;
      const saved = response?.data?.data?.data;
      if (!saved || saved._id !== identity) throw new Error('Invalid save response');
      const accepted = accept(saved);
      if (accepted) options.current.onSaved?.(saved);
      if (draftVersion.current === draftAtSubmit) setDraft('');
      if (!accepted) { setNotice('Saved, but a newer video is already current.'); return; }
      if (publicationRevision(saved) <= revisionAtSubmit || saved.coverMediaPublication?.intent !== intent) {
        setNotice('Saved. Live updates are unavailable until the video service is updated.'); return;
      }
      setNotice('Saved. Sending video update…');
      await publishSaved(saved);
    } catch (_) {
      if (live.current) setNotice('Could not save this video. Your draft is retained; check your access and retry.');
    } finally { submitting.current = false; if (live.current) setBusy(false); }
  }
  async function retryBroadcast() {
    if (submitting.current || !retry || !permissions.live) return;
    submitting.current = true; setBusy(true);
    try {
      const saved = await refresh();
      if (!live.current) return;
      if (publicationRevision(saved) !== retry.revision) {
        setRetry(null); setNotice('A newer video was saved. This older update will not be sent.'); return;
      }
      await publishSaved(saved);
    } catch (_) { if (live.current) setNotice('Saved; live update not confirmed. Check your connection and retry.'); }
    finally { submitting.current = false; if (live.current) setBusy(false); }
  }
  function uploaded(saved) {
    if (!live.current || !accept(saved)) return;
    setPreview(null); options.current.onSaved?.(saved);
    publishSaved(saved);
  }

  const media = snapshot ? parseGalaxyVideoUrl(snapshot.coverMedia) : EMPTY;
  const video = isGalaxyVideo(media);
  const draftMedia = parseGalaxyVideoUrl(draft);
  if (!galaxy || (!permissions.view && !permissions.save)) return null;
  return <section className="galaxy-video" aria-label="Galaxy video">
    <div className="galaxy-video__header">
      <h2>Galaxy video</h2>
      <div className="galaxy-video__actions">
        {permissions.view && (video || media.kind === 'image') && <button type="button" aria-expanded={visible}
          aria-controls="galaxy-published-video" onClick={() => { cancelPlayback(); setPreview(null); setVisible((value) => !value); }}>
          {visible ? 'Hide video' : 'Show video'}</button>}
        {permissions.upload && renderUpload?.(uploaded)}
      </div>
    </div>
    {visible && permissions.view && <div id="galaxy-published-video">
      {showAd ? ad : preview ? <p className="galaxy-video__hint">Saved video paused while you preview.</p>
        : video ? <GalaxyVideoPlayer key={playerEpoch} media={media} autoPlay={playRequest !== null}
          onPause={cancelPlayback} onPlaying={() => { pending.current = null; }} />
        : media.kind === 'image' ? <img className="galaxy-video__cover" src={media.url} alt={`${galaxy.name} cover`} /> : null}
      {showAd && playRequest !== null && <button type="button" onClick={cancelPlayback}>Pause pending video</button>}
    </div>}
    {permissions.view && viewerRecovery && <div className="galaxy-video__hint" role="status">
      <p>Could not load the video update. Try again.</p>
      <button type="button" disabled={refreshingVideo} onClick={() => {
        if (viewerRecovery.event) loadPublication(viewerRecovery);
        else catchUp();
      }}>Retry video update</button>
    </div>}
    {permissions.save && <form className="galaxy-video__editor" onSubmit={(event) => { event.preventDefault(); save('save'); }}>
      <label htmlFor="galaxy-video-link">Video link</label>
      <input id="galaxy-video-link" type="text" inputMode="url" autoComplete="off" spellCheck="false"
        placeholder="Paste a YouTube, Cloudinary, or direct video link" value={draft}
        aria-invalid={Boolean(fieldError)} aria-describedby="galaxy-video-feedback"
        onChange={(event) => { draftVersion.current++; setDraft(event.target.value); setFieldError(''); }} />
      <div className="galaxy-video__actions">
        <button type="button" disabled={busy || !isGalaxyVideo(draftMedia) || (visible && showAd)} onClick={() => {
          cancelPlayback(); setPlayerEpoch((value) => value + 1); setPreview(draftMedia);
        }}>Preview</button>
        <button type="submit" disabled={busy || !draft.trim()}>{busy ? 'Saving…' : 'Save video'}</button>
        <button type="button" className="galaxy-video__live" disabled={busy || !draft.trim() || !permissions.live}
          onClick={() => save('go-live')}>Go Live</button>
      </div>
      <p className="galaxy-video__hint">Save keeps viewers paused. Go Live asks connected viewers to play.</p>
      {!permissions.live && <p className="galaxy-video__hint">Go Live requires Galaxy membership and an assigned Galaxy role.</p>}
      <div id="galaxy-video-feedback" aria-live="polite">{fieldError || notice}</div>
      {retry && <button type="button" disabled={busy || !permissions.live} onClick={retryBroadcast}>Retry live update</button>}
      {preview && <div className="galaxy-video__preview" aria-label="Local video preview">
        <div className="galaxy-video__header"><h3>Preview · only you</h3><button type="button" onClick={() => setPreview(null)}>Close preview</button></div>
        <GalaxyVideoPlayer key={preview.url} media={preview} />
      </div>}
    </form>}
  </section>;
});

export default forwardRef(function GalaxyVideo(props, ref) {
  const permissions = galaxyVideoPermissions(props.galaxy, props.player);
  // Revoke the whole media lifetime on account/route/access changes.
  const identity = `${props.routeId}:${props.player?.data?.player?._id}:${props.player?.token}:${permissions.view}:${permissions.save}`;
  return <GalaxyVideoSession {...props} permissions={permissions} key={identity} ref={ref} />;
});
